import { login as loginAPI } from '@/remote/auth/login';
import { ROUTES } from '@/router/routes';
import type { LoginData, LoginState } from '@/types/login';
import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';

const initialLoginData: LoginData = {
  id: '',
  password: '',
  isLoggedIn: false,
};

export const useLoginStore = create<LoginState>()(
  persist(
    (set, get) => ({
      loginData: initialLoginData,
      userInfo: null,
      accessToken: null,
      isLoading: false,
      error: null,
      rememberMe: false,

      updateLoginData: (field, value) =>
        set((state) => ({
          loginData: {
            ...state.loginData,
            [field]: value,
          },
        })),

      setLoading: (loading) => set({ isLoading: loading }),

      setUserInfo: (userInfo) => set({ userInfo }),

      setAccessToken: (token) => set({ accessToken: token }),

      setError: (error) => set({ error }),

      setRememberMe: (rememberMe) => set({ rememberMe }),

      resetLoginData: () =>
        set({
          loginData: initialLoginData,
          userInfo: null,
          accessToken: null,
          error: null,
        }),

      login: async (navigate) => {
        const {
          loginData,
          rememberMe,
          setLoading,
          setUserInfo,
          setAccessToken,
          setError,
        } = get();

        setLoading(true);
        setError(null);

        try {
          const response = await loginAPI({
            loginId: loginData.id,
            password: loginData.password,
          });

          // 로그인 성공 시 상태 업데이트
          setUserInfo(response.user);
          setAccessToken(response.accessToken);
          set((state) => ({
            loginData: {
              ...state.loginData,
              id: rememberMe ? state.loginData.id : '', // 기억하기 체크 시에만 ID 저장
              password: '', // 보안상 비밀번호는 항상 초기화
              isLoggedIn: true,
            },
          }));

          // 대시보드로 이동
          navigate(ROUTES.SITE_INFORMATION);
        } catch (error) {
          const errorMessage =
            error instanceof Error ? '아이디와 비밀번호를 확인해 주세요.' : '';
          setError(errorMessage);
        } finally {
          setLoading(false);
        }
      },
    }),
    {
      name: 'login-storage', // localStorage key name
      storage: createJSONStorage(() => localStorage),
      // 보안상 비밀번호는 저장하지 않음, 토큰과 사용자 정보는 저장
      partialize: (state) => ({
        loginData: {
          id: state.rememberMe ? state.loginData.id : '', // rememberMe가 true일 때만 ID 저장
          password: '', // 비밀번호는 저장하지 않음
          isLoggedIn: state.loginData.isLoggedIn,
        },
        userInfo: state.userInfo,
        accessToken: state.accessToken,
        rememberMe: state.rememberMe,
      }),
    }
  )
);
